/**
 * <s-inline-video> — muted, looping background or showcase video.
 *
 * Plays only while on screen and never autoplays for shoppers who prefer reduced motion
 * (they get native controls instead). An optional [data-video-mute] button toggles sound.
 *
 * Required markup:
 *   video[muted][loop][playsinline] — the video element
 *   button[data-video-mute]         — optional sound toggle with aria-pressed
 */
class SInlineVideo extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.#ac = new AbortController();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.video.controls = true;
      return;
    }

    this.video.addEventListener('playing', () => this.setAttribute('data-playing', ''), { signal: this.#ac.signal });
    this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0.35 });
    this.#observer.observe(this);
    this.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.video.play().catch(() => {
          this.video.controls = true;
        });
      } else {
        this.video.pause();
      }
    }
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-video-mute]');
    if (!toggle) return;
    this.video.muted = !this.video.muted;
    toggle.setAttribute('aria-pressed', String(!this.video.muted));
  };
}

if (!customElements.get('s-inline-video')) {
  customElements.define('s-inline-video', SInlineVideo);
}
